import { yupResolver } from '@hookform/resolvers/yup';
import CountryListModule from 'country-list-with-dial-code-and-flag';
import { isValidPhoneNumber } from 'libphonenumber-js';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { RootState } from '../../../../redux/types';
import { Person } from '../../../../types/instant-lease-api';
import { emailAlreadyUsed } from '../../../../utils/helper';
import FormLayout from '../../../common/form-layout/form-layout';
import InputField from '../../../common/input-field/input-field';
import TwoInputFields from '../../../common/two-input-fields/two-input-fields';
import Button from '../../components/button/button';
import InternationalPhoneInput from '../../components/international-phone-input/international-phone-input';

// Handle CJS/ESM interop – the actual instance may be on `.default`
const CountryList = ((CountryListModule as any).default ??
  CountryListModule) as typeof CountryListModule;

const getDialCodeDetails = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return {
      dialCode: '+49',
      dialCodeLength: 0,
    };
  }

  let dialCodeLength = 0;
  for (let i = 0; i <= 5; i++) {
    const matchedCountries = CountryList.findByDialCode(
      phoneNumber.slice(0, i),
    );
    if (matchedCountries.length === 1) {
      dialCodeLength = i;
    }
  }

  return {
    dialCode: phoneNumber.slice(0, dialCodeLength) || '+49',
    dialCodeLength,
  };
};

export interface ContactDataSubmittedValues {
  email: string;
  city: string;
  street: string;
  house_number: string;
  zip: string;
  phone_number: string;
}

interface ContactDataProps {
  onSubmit: (signer: ContactDataSubmittedValues) => void;
  signer?: Person;
  setError?: (t: boolean) => void;
  showSummary?: boolean;
  loading?: boolean;
}

const ContactData: React.FC<ContactDataProps> = ({
  onSubmit,
  signer,
  setError,
  loading,
}) => {
  // Will be used later
  const {
    leaseApp: { activeApplication },
  } = useSelector((state: RootState) => state);

  const signerPhoneNumber =
    signer?.contact_details?.phone_numbers?.[0]?.value || '';
  const { dialCode: initialDialCode } = getDialCodeDetails(signerPhoneNumber);

  // Set country code when user pick flag
  const [countryCode, setCountry] = useState<string>(initialDialCode);

  // React 19 pattern: adjust state during render when source data changes
  const [prevSignerPhoneNumber, setPrevSignerPhoneNumber] =
    useState(signerPhoneNumber);
  if (signerPhoneNumber !== prevSignerPhoneNumber) {
    setPrevSignerPhoneNumber(signerPhoneNumber);
    setCountry(getDialCodeDetails(signerPhoneNumber).dialCode);
  }

  // Form validation
  const validationSchema = yup.object({
    email: yup
      .string()
      .email('Bitte vollständige Email Adresse eintragen')
      .test('email', 'E-Mail bereits genutzt', (value) => {
        if (value) {
          if (emailAlreadyUsed(activeApplication, value, signer)) {
            return false;
          }
        }
        return true;
      })
      .required('Pflichtfeld'),
    city: yup
      .string()
      .trim()
      .matches(/^(|.{2,})$/)
      .required('Pflichtfeld'),
    street: yup.string().required('Pflichtfeld'),
    house_number: yup.string().required('Pflichtfeld'),
    zip: yup.string().required('Pflichtfeld'),
    phone_number: yup
      .string()
      .matches(
        /^[1-9][0-9]+$/gm,
        'Bitte geben Sie eine gültige Handynummer ohne führende 0 an, z.B.: 40471040',
      )
      .test(
        'is-valid-phone',
        'Bitte geben Sie eine gültige Handynummer ohne führende 0 an, z.B.: 40471040',
        (value) => {
          if (!value) return false;
          const fullNumber = `${countryCode}${value}`;
          return isValidPhoneNumber(fullNumber);
        },
      )
      .required('Pflichtfeld'),
  });

  // Form Hook
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    trigger,
    formState: { isDirty },
  } = useForm<ContactDataSubmittedValues>({
    mode: 'onTouched',
    defaultValues: {},
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (
      !_.isEmpty(signer?.contact_details) &&
      signer?.contact_details?.emails?.length &&
      signer?.contact_details?.phone_numbers?.length &&
      signer?.contact_details?.addresses?.length
    ) {
      const { dialCodeLength } = getDialCodeDetails(signerPhoneNumber);

      // Remove intl code from number
      const phoneNumberWithoutIntlCode = signerPhoneNumber
        .substring(dialCodeLength)
        .replace(/\s+/g, '');

      setValue('email', signer?.contact_details?.emails[0]?.value || '', {
        shouldValidate: signer?.contact_details?.emails[0]?.value === '',
      });
      setValue('phone_number', phoneNumberWithoutIntlCode || '', {
        shouldValidate: phoneNumberWithoutIntlCode === '',
      });
      setValue(
        'street',
        signer?.contact_details?.addresses[0].street_line?.split(' ')[0] || '',
        {
          shouldValidate:
            signer?.contact_details?.addresses[0].street_line === '',
        },
      );
      setValue(
        'house_number',
        signer?.contact_details?.addresses[0].street_line?.split(' ')[1] || '',
        {
          shouldValidate:
            signer?.contact_details?.addresses[0].street_line === '',
        },
      );
      setValue('city', signer?.contact_details?.addresses[0].locality || '', {
        shouldValidate: signer?.contact_details?.addresses[0].locality === '',
      });
      setValue('zip', signer?.contact_details?.addresses[0].postal_code || '', {
        shouldValidate:
          signer?.contact_details?.addresses[0].postal_code === '',
      });
      trigger();
    }
  }, [setValue, signer, signerPhoneNumber, trigger]);

  // hook controlling error
  useEffect(() => {
    if (setError && isDirty) {
      setError(_.isEmpty(errors));
    }
  });

  // Form submit handler
  const formSubmitHandler = (values: ContactDataSubmittedValues) => {
    onSubmit({
      email: values.email,
      phone_number: `${countryCode}${values.phone_number}`,
      zip: values.zip,
      house_number: values.house_number,
      street: values.street,
      city: values.city,
    });
  };

  return (
    <FormLayout sectionTitle='Kontaktdaten'>
      <form onSubmit={handleSubmit(formSubmitHandler)}>
        <TwoInputFields
          tooltip='Wie lautet Deine private Meldeadresse?'
          label='Straße | Hausnummer'
          gridTemplateColumns='70% 25%'
        >
          <InputField {...register('street')} error={errors?.street?.message} />
          <InputField
            {...register('house_number')}
            error={errors?.house_number?.message}
          />
        </TwoInputFields>
        <TwoInputFields
          label='Postleitzahl | Wohnort'
          gridTemplateColumns='25% 70%'
        >
          <InputField
            {...register('zip')}
            type='number'
            maxLength={5}
            withErrorMessage={false}
            error={errors.zip?.message}
          />
          <InputField
            {...register('city')}
            withErrorMessage={false}
            error={errors.city?.message}
          />
        </TwoInputFields>
        <InputField
          label='E-Mail-Adresse'
          {...register('email')}
          error={errors?.email?.message}
        />
        <InternationalPhoneInput
          label='Mobiltelefonnummer'
          getCode={(value) => setCountry(value)}
          code={countryCode}
          placeholder='40471040'
          {...register('phone_number')}
          error={errors?.phone_number?.message}
        />
        <div className='add-additional-person'>
          <Button
            theme='secondary'
            type='button'
            className='save-button next button-text save-and-next-tab'
            role='presentation'
            loading={loading}
            onClick={handleSubmit(formSubmitHandler)}
          >
            nächster Schritt
          </Button>
        </div>
      </form>
    </FormLayout>
  );
};

export default ContactData;
